import { CUSTOM_TILES } from '../custom-tiles.js';
import { TILE_OVERRIDES } from '../tile-overrides.js';
import { WATER_BACKDROP, SAND_COAST } from './water.js';
import { GRASS_SURFACE, DARK_GRASS_SURFACE, DIRT_SURFACE, ROCK_SURFACE } from './surfaces.js';
import { OUTDOOR_FLOOR_TILES, INTERIOR_FLOOR_TILES } from './floors.js';
import { CLIFF_ART, HEIGHT_OVERLAY_ART, RAMP_ART, STAIR_ART } from './elevation.js';
import { EXTERIOR_AND_ROOF_TILES } from './buildings.js';
import {
  INTERIOR_PARTITION_AND_FURNITURE,
  INTERIOR_PROPS,
  EXTRA_INTERIOR_WALL_OBJECTS,
  EXTRA_INTERIOR_WALLS,
} from './interiors.js';
import { TOWN_OBJECTS, LARGE_TOWN_OBJECTS, LIGHT_POSTS, MODULAR_TABLE, WOODEN_FENCE } from './props.js';
import { SHRUBS_AND_SAPLINGS, FLOWERS, GRASSES, HEDGE_ART } from './flora.js';
import { STATIC_TREES } from './trees.js';

export { MASK, normalizeBlobMask, PIPE_MASKS, BLOB_MASKS } from './masks.js';

export const SOURCE_TILE_SIZE = 16;
// 'eave' is a roof's overhang, drawn one cell south of the roof's front edge.
export const LAYERS = ['ground', 'onGround', 'wall', 'roof', 'eave'];
export const MAP_CHANNELS = ['foundation', 'surface', 'outdoorFloor', 'exterior', 'interiorFloor', 'onGround', 'treeWood', 'wall', 'wallObject', 'roof'];

export const TILESET = [
  WATER_BACKDROP,
  SAND_COAST,
  GRASS_SURFACE,
  DARK_GRASS_SURFACE,
  DIRT_SURFACE,
  ROCK_SURFACE,
  ...OUTDOOR_FLOOR_TILES,
  CLIFF_ART,
  HEIGHT_OVERLAY_ART,
  RAMP_ART,
  STAIR_ART,
  ...EXTERIOR_AND_ROOF_TILES,
  ...INTERIOR_FLOOR_TILES,
  ...INTERIOR_PARTITION_AND_FURNITURE,
  ...INTERIOR_PROPS,
  ...TOWN_OBJECTS,
  ...LARGE_TOWN_OBJECTS,
  ...LIGHT_POSTS,
  MODULAR_TABLE,
  WOODEN_FENCE,
  ...SHRUBS_AND_SAPLINGS,
  ...FLOWERS,
  ...GRASSES,
  HEDGE_ART,
  ...STATIC_TREES,
  ...CUSTOM_TILES,
  ...EXTRA_INTERIOR_WALL_OBJECTS,
  ...EXTRA_INTERIOR_WALLS,
];

/**
 * Apply designer pixel overrides onto a tileset. `$animation` writes go to
 * `variantFrames` when present, otherwise to grid-based `animation.frames`.
 * Destination containers are copied first when they alias `components`, so an
 * override of frame 0 cannot silently rewrite the static ground layer.
 */
export function applyTileOverrides(tiles, overrides) {
  for (const tile of tiles) {
    const tileOverrides = overrides[tile.key];
    if (!tileOverrides) continue;
    for (const [layer, variants] of Object.entries(tileOverrides)) {
      if (layer === '$animation') continue;
      if (!tile.components[layer]) continue;
      for (const [mask, pixels] of Object.entries(variants)) tile.components[layer][mask] = pixels;
    }
    if (!tile.animation) continue;
    const component = tile.animation.component;
    for (const [frame, layers] of Object.entries(tileOverrides.$animation ?? {})) {
      for (const [layer, variants] of Object.entries(layers)) {
        if (layer !== component) continue;
        const frameIndex = Number(frame);
        if (tile.animation.variantFrames) {
          const frames = tile.animation.variantFrames;
          let target = frames[frameIndex];
          if (!target) continue;
          if (target === tile.components[layer]) {
            target = { ...target };
            frames[frameIndex] = target;
          }
          for (const [mask, pixels] of Object.entries(variants)) target[mask] = pixels;
          continue;
        }
        const frames = tile.animation.frames;
        if (!frames || frames[frameIndex] === undefined) continue;
        if (tile.animation.gridSize) {
          const source = frames[frameIndex];
          if (!Array.isArray(source)) continue;
          const target = source.slice();
          frames[frameIndex] = target;
          for (const [mask, pixels] of Object.entries(variants)) target[Number(mask)] = pixels;
        } else {
          const pixels = variants[0] ?? variants['0'];
          if (pixels) frames[frameIndex] = pixels;
        }
      }
    }
  }
}

// Browser edits are source-controlled separately and override generated defaults.
applyTileOverrides(TILESET, TILE_OVERRIDES);
